<template>
    <section :class="css.container" class="container" ref="container">
        <!-- * 描述信息（不管是否存在译文, 仅存在原文展示） -->
        <div :class="css.info">
            <div class="text-hint" v-if="!origin.descript.origin">{{ $t("Placeholder.NotModuleDescript") }}</div>
            <template v-else>
                <div>{{ origin.descript.default }}</div>
                <AIStatement :class="css.statement" :name="$t('BasicInformation')" subject="shallow" :contexts="origin.descript" type="Gene" />
            </template>
        </div>

        <!-- * 相关信息 -->
        <div :class="css.ids">
            <div class="font-bold color-1">{{ $t("RelatedID") }}：</div>
            <div :class="css.content">
                <template v-if="!origin?.related_ids?.length">{{ $t("NoDataAvailable") }}</template>
                <template v-else>
                    <div :class="css.id" v-for="(related, key) in origin.related_ids" :key="key">
                        <template v-if="related.href">
                            <project-route :class="css_normalize.assets" :to="related.href">{{ related.name }}：{{ related.id }}</project-route>
                        </template>
                        <template v-else>
                            <div>{{ related.name }}：{{ related.id }}</div>
                        </template>
                    </div>
                </template>
            </div>
        </div>

        <!-- * 基本信息 -->
        <div :class="css.body">
            <!-- * 标题 -->
            <div :class="css.name" class="font-bold font-variable color-1">{{ $t("BasicInformation") }}</div>
            <!-- * 表格 -->
            <ui-table2 :class="css.table" :option="options.info" :data="[origin.info]">
                <template #size>{{ origin?.info?.size ? `${origin.info.size} bp` : "--" }}</template>
                <template #ncbi>
                    <template v-if="!origin?.info?.ncbi">--</template>
                    <template v-else>
                        <project-route :class="css_normalize.assets" :to="`https://www.ncbi.nlm.nih.gov/gene/${origin.info.ncbi}`">
                            {{ origin.info.ncbi }}
                        </project-route>
                    </template>
                </template>

                <template #mutations>
                    <template v-if="!origin?.info?.mutations">--</template>
                    <ui-tooltip v-else :class-extra-name="css.view" direction="bottom">
                        <div :class="css_normalize.assets" @click="Identify.mutation(origin.info.ncbi, Formats.query($route.query.keyword))">
                            {{ origin.info.mutations }}
                        </div>
                        <template #content>
                            <div :class="css.content">{{ $t("Pathogenic") }}: {{ origin?.mutations?.pathogenic || 0 }}</div>
                            <div :class="css.content">{{ $t("LikelyPathogenic") }}: {{ origin?.mutations?.pathogenic_likely || 0 }}</div>
                            <div :class="css.content">{{ $t("LikelyBenign") }}: {{ origin?.mutations?.benign_likely || 0 }}</div>
                            <div :class="css.content">{{ $t("Benign") }}: {{ origin?.mutations?.benign || 0 }}</div>
                            <div :class="css.content">{{ $t("Uncertain") }}: {{ origin?.mutations?.uncertain || 0 }}</div>
                        </template>
                    </ui-tooltip>
                </template>

                <template #references>
                    <template v-if="!origin?.info?.references">--</template>
                    <template v-else>
                        <div :class="css_normalize.assets" @click="$emit('move8')">{{ origin.info.references }}</div>
                    </template>
                </template>

                <template #transcripts>
                    <template v-if="!origin?.info?.transcripts">--</template>
                    <template v-else>
                        <div :class="css_normalize.assets" @click="$emit('move4')">{{ origin.info.transcripts }}</div>
                    </template>
                </template>

                <template #related_models>
                    <template v-if="!origin?.info?.related_models?.length">--</template>
                    <div v-else :class="css_normalize.assets" @click="$emit('move9')">{{ origin.info.related_models.length }}</div>
                </template>

                <template #related_diseases>
                    <template v-if="!origin?.info?.related_diseases">--</template>
                    <template v-else>
                        <div :class="css_normalize.assets" @click="$emit('move3')">{{ origin.info.related_diseases }}</div>
                    </template>
                </template>
            </ui-table2>

            <!-- * 可视化模型 -->
            <div :class="css.visualization">
                <div :class="css.header">
                    <div :class="css.name" class="font-bold font-variable color-1">
                        {{ $t("GeneGeneticsInfo", { name: origin.symbol, descript: origin.strand }) }}
                    </div>
                    <div :class="css.name" class="font-bold font-variable color-1">{{ origin.version }}</div>
                </div>

                <div :class="css.content">
                    <genetic-information ref="visualization" />
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
//* 组件库引入
import { UiTable2, UiTooltip } from "various-ui";
//* 公共组件引入
import { AIStatement } from "@/components/controls";
import { GeneticInformation } from "@/components/models";
//* 公共函数
import { Identify, Formats } from "@/assets/js";
//* 组件属性
import { ModulePropsOption } from "./index";
import { useComposable } from "./src/composable";
//* 样式
import css from "./style/index.module.less";
import css_normalize from "@/assets/style/src/index.module.less";

//* 组件属性注册
const define = defineProps(ModulePropsOption);

//* 组合函数
const { nodes, options } = useComposable(define);
const { container, visualization } = nodes;

//* 组件挂载时初始化绘图组件
onMounted(() => {
    if (nodes.container.value) {
        useResizeWObserver(nodes.container.value, () => nodes.visualization.value?.init(define.origin, 56));
    }
});
</script>
